import { Fragment, FunctionComponent, useState } from "react";
import { SetPageTitle } from "../../components/SetPageTitle";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCandidate } from "../../api/hooks/useCandidate";
import {
    Button,
    Badge,
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Card,
    CardBody,
    Divider,
    Flex,
    Grid,
    GridItem,
    Heading,
    HStack,
    Icon,
    IconButton,
    Link,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Td,
    Text,
    Textarea,
    Tooltip,
    Tr,
    useDisclosure,
    VStack,
    Stack,
} from "@chakra-ui/react";
import {
    CandidateQualification,
    displayEmploymentStatus,
    displayQualification,
    displayZone,
    EntityCandidate,
    EntityCandidateForUpdate,
} from "../../api/models/EntityCandidate";
import { CandidateStatusIcon, getCandidateStatusTitle } from "../../components/CandidateStatus";
import { IconDownload, IconEdit } from "@tabler/icons-react";
import { CandidateTimeline } from "./CandidateTimeline";
import { useClient, useClientEntity, useRequestDownloadFileFn, useRequests, useGenerate8850 } from "../../api";
import { useTenant } from "../../api/api";
import { DownloadLink, formatDateStr, formatPhone, formatSsn, Shimmer, TableResult } from "@am-tax/fe-core";
import { useUserContext } from "../../context";
import { displayRequestStatus, getStatusIcon } from "@am-tax/wotc-common";
import { CandidateQuestionnaire } from "./CandidateQuestionnaire";
import { useAreaPermissions, useBoolean } from "../../hooks";
import { CandidatesArea } from "../../auth";
import { useCandidateTimeline } from "../../api/hooks/useCandidateTimeline";
import { useCandidateCommentCreate } from "../../api/hooks/useCandidateCommentCreate";
import { useCandidateComments } from "../../api/hooks/useCandidateComments";
import { CandidateCommentCards } from "./CandidateComments";
import { CandidateCommentProps } from "../../api/models/CandidateComment";
import globalEvents from "../../utils/globalEvents";
import { EntityInfoPopover } from "./EntityInfoPopover";
import { CandidateGeneralModal } from "./CandidateGeneralModal";
import { CandidateEligibilityModal } from "./CandidateEligibilityModal";
import { CandidateEmploymentModal } from "./CandidateEmploymentModal";
import { CandidateAdditionalModal } from "./CandidateAdditionalModal";
import { CandidateWageHistorySection } from "./CandidateWageHistorySection";
import { CreateRequestModal } from "../CreateRequest";
import { RequestModal } from "../RequestPage";
import { CandidateDetailsPdfViewerModal } from "../CandidateDetailsPage";

const socialSecurityNumberWithoutDashesLength = 9;
const socialSecurityNumberWithDashesLength = 11;

type CandidatePageData = EntityCandidateForUpdate;

export const CandidatePage: FunctionComponent = () => {
    const { cId, eId, candidateId } = useParams();
    const entityQuery = useClientEntity(cId || "", eId || "", { enabled: true });
    const tenantQuery = useTenant(cId);
    const candidateFieldsConfiguration = tenantQuery.data?.candidateFieldsConfiguration;
    const clientQuery = useClient(cId);
    const areaPermissions = useAreaPermissions(CandidatesArea, cId);
    const navigate = useNavigate();
    const downloadFn = useRequestDownloadFileFn();
    const candidateQuery = useCandidate(cId, eId, candidateId);
    const candidate = (candidateQuery.data || {}) as CandidatePageData;
    const refetchCandidateQuery = candidateQuery.refetch;
    globalEvents.registerEvent("refetchCandidatePageQuery", refetchCandidateQuery);
    const candidateLoading = candidateQuery.isLoading;
    const requestsQuery = useRequests(cId, eId, candidateId);
    const requests = requestsQuery.data || null;
    const generate8850 = useGenerate8850();

    const { dateFormat } = useUserContext();

    const showEligibility = true;
    const showEmployment = true;

    const [showFullSsn, fullSsn] = useBoolean(false);

    const generalEdit = useDisclosure();
    const eligibilityEdit = useDisclosure();
    const employmentEdit = useDisclosure();
    const additionalEdit = useDisclosure();
    const createRequestModal = useDisclosure();
    const requestEditModal = useDisclosure();
    const [selectedRequestId, setSelectedRequestId] = useState<string | undefined>(undefined);
    const pdfViewerModal = useDisclosure();
    const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>(undefined);

    const candidateTimelineQuery = useCandidateTimeline(cId, eId, candidateId);

    // ---- CandidateComment Components  ----
    const [comment, setComment] = useState("");
    const candidateComments = useCandidateComments(cId, eId, candidateId);

    //  ---- Create CandidateComment handler ----
    const saveCandidateComment = useCandidateCommentCreate();
    const createCandidateComment = async (comment: string) => {
        const props: CandidateCommentProps = {
            tenantId: cId,
            entityId: eId,
            candidateId: candidateId,
            commentBody: comment,
        };
        saveCandidateComment.mutate(props, {
            onSuccess: async () => {
                setComment("");
                await candidateComments.refetch();
            },
        });
    };

    const navigateToCandidates = () => {
        navigate(`/clients/${cId}/candidates?eId=${eId}`);
    };

    function showMissing(value: string | number | null | undefined, missingText?: string) {
        return value ? <Text>{value}</Text> : <Text color={"gray.200"}>{missingText || "missing"}</Text>;
    }

    const commentCount = candidateComments.data?.length ?? 0;
    const documentCount = requests?.reduce((sum, r) => sum + (r.documents?.length || 1), 0) ?? 0;

    const [searchParams, setSearchParams] = useSearchParams();
    const tabSlugs = [
        ...(showEmployment && areaPermissions.has(CandidatesArea.read_full) ? ["details"] : []),
        ...(areaPermissions.has(CandidatesArea.request_mgmt) ? ["documents"] : []),
        ...(areaPermissions.hasAny(CandidatesArea.read_basic, CandidatesArea.read_full) ? ["timeline"] : []),
        ...(areaPermissions.has(CandidatesArea.questionnaire) ? ["questionnaire"] : []),
        "comments",
    ];
    const tabParam = searchParams.get("tab") ?? "";
    const tabIndex = tabSlugs.indexOf(tabParam) >= 0 ? tabSlugs.indexOf(tabParam) : 0;
    const handleTabChange = (index: number) => {
        setSearchParams(
            prev => {
                prev.set("tab", tabSlugs[index]);
                return prev;
            },
            { replace: true }
        );
    };

    return (
        <>
            <Breadcrumb mt={"-1.5rem"} mb={"1.5rem"}>
                <BreadcrumbItem>
                    <BreadcrumbLink onClick={navigateToCandidates}>Candidates</BreadcrumbLink>
                </BreadcrumbItem>

                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink>
                        <Shimmer isLoading={candidateLoading}>
                            {candidate?.firstName} {candidate?.lastName}
                        </Shimmer>
                    </BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            <Flex direction={"column"} gap={"1.5rem"}>
                <SetPageTitle title={"Candidate Details"} />

                {/* ── Persistent Summary Card ───────────────────────────────── */}
                <Card variant={"withShadow"}>
                    <CardBody p={"1.5rem 2rem"}>
                        {/* Header row: name + status + edit button */}
                        <Flex justifyContent={"space-between"} alignItems={"flex-start"} mb={"1rem"}>
                            <VStack align={"start"} spacing={".15rem"}>
                                <Shimmer isLoading={candidateLoading}>
                                    <Heading variant={"cardHeading"} mb={0}>
                                        {candidate?.firstName} {candidate?.middleName} {candidate?.lastName}
                                    </Heading>
                                </Shimmer>
                                {/* Client & Entity context */}
                                <HStack spacing={".75rem"} flexWrap={"wrap"}>
                                    <Shimmer isLoading={clientQuery.isLoading}>
                                        <Text variant={"label"} fontSize={"sm"}>
                                            {clientQuery.data?.name ?? "—"}
                                        </Text>
                                    </Shimmer>
                                    {clientQuery.data?.name && (
                                        <Text color={"gray.300"} fontSize={"sm"}>
                                            |
                                        </Text>
                                    )}
                                    <Shimmer isLoading={entityQuery.isLoading}>
                                        <HStack spacing={".25rem"}>
                                            <Text fontSize={"sm"}>{entityQuery.data?.name ?? candidate?.entityName ?? "—"}</Text>
                                            <EntityInfoPopover clientId={cId || ""} entity={entityQuery.data} isLoading={entityQuery.isLoading} />
                                        </HStack>
                                    </Shimmer>
                                </HStack>
                            </VStack>

                            <HStack spacing={"1rem"}>
                                <Shimmer isLoading={candidateLoading}>
                                    <HStack>
                                        <CandidateStatusIcon status={candidate?.status} boxSize={"1.5rem"} />
                                        <Box fontWeight={"medium"}>{getCandidateStatusTitle(candidate?.status)}</Box>
                                    </HStack>
                                </Shimmer>
                                {areaPermissions.has(CandidatesArea.edit) && (
                                    <Tooltip label="Edit general info">
                                        <IconButton icon={<Icon as={IconEdit} />} aria-label={"Edit General Info"} onClick={generalEdit.onOpen} />
                                    </Tooltip>
                                )}
                            </HStack>
                        </Flex>

                        <Divider mb={"1rem"} />

                        {/* Personal info grid */}
                        {areaPermissions.hasAny(CandidatesArea.read_basic, CandidatesArea.read_full) && (
                            <Grid gridTemplateColumns={"auto 1fr auto 1fr"} columnGap={"1.5rem"} rowGap={".35rem"} alignItems={"center"}>
                                <GridItem colSpan={2} rowSpan={3}>
                                    <VStack align={"start"} spacing={".1rem"}>
                                        {candidate?.street1 ? (
                                            <>
                                                <Shimmer isLoading={candidateLoading}>
                                                    <Text>{candidate?.street1}</Text>
                                                </Shimmer>
                                                {candidate?.street2 && <Text>{candidate?.street2}</Text>}
                                                <Shimmer isLoading={candidateLoading}>
                                                    <Text>{formatCandidateAddressLine3(candidate)}</Text>
                                                </Shimmer>
                                            </>
                                        ) : (
                                            showMissing(candidate?.street1, "address missing")
                                        )}
                                    </VStack>
                                </GridItem>

                                <GridItem>
                                    <Text variant={"label"}>Email:</Text>
                                </GridItem>
                                <GridItem>
                                    <Shimmer isLoading={candidateLoading}>{showMissing(candidate?.email)}</Shimmer>
                                </GridItem>
                                <GridItem>
                                    <Text variant={"label"}>Phone:</Text>
                                </GridItem>
                                <GridItem>
                                    <Shimmer isLoading={candidateLoading}>{showMissing(formatPhone(candidate?.phone))}</Shimmer>
                                </GridItem>
                                <GridItem>
                                    <Text variant={"label"}>Birth:</Text>
                                </GridItem>
                                <GridItem>
                                    <Shimmer isLoading={candidateLoading}>{showMissing(formatDateStr(candidate?.dateOfBirth, dateFormat))}</Shimmer>
                                </GridItem>

                                <GridItem>
                                    <Text variant={"label"}>County:</Text>
                                </GridItem>
                                <GridItem>
                                    <Shimmer isLoading={candidateLoading}>{showMissing(candidate?.county)}</Shimmer>
                                </GridItem>
                                <GridItem>
                                    <Text variant={"label"}>
                                        SSN (<Link onClick={fullSsn.toggle}>{showFullSsn ? "hide" : "show"} full</Link>):
                                    </Text>
                                </GridItem>
                                <GridItem>
                                    <Shimmer isLoading={candidateLoading}>
                                        <pre>
                                            {showMissing(
                                                (() => {
                                                    if (candidate?.socialSecurityNumber) {
                                                        const ssnLength = candidate.socialSecurityNumber.length;
                                                        if (
                                                            ssnLength === socialSecurityNumberWithoutDashesLength ||
                                                            ssnLength === socialSecurityNumberWithDashesLength
                                                        ) {
                                                            if (showFullSsn) {
                                                                return ssnLength === socialSecurityNumberWithoutDashesLength
                                                                    ? formatSsn(candidate.socialSecurityNumber)
                                                                    : candidate.socialSecurityNumber;
                                                            } else {
                                                                return ssnLength === socialSecurityNumberWithoutDashesLength
                                                                    ? "***-**-" + candidate.socialSecurityNumber.substring(5)
                                                                    : "***-**-" + candidate.socialSecurityNumber.substring(7);
                                                            }
                                                        }
                                                    }
                                                    return undefined;
                                                })()
                                            )}
                                        </pre>
                                    </Shimmer>
                                </GridItem>

                                {candidate?.designatedZoneType && (
                                    <GridItem colSpan={4}>
                                        <Tooltip label={candidate.designatedZone}>
                                            <Badge colorScheme={"green"}>{displayZone(candidate.designatedZoneType)}</Badge>
                                        </Tooltip>
                                    </GridItem>
                                )}
                            </Grid>
                        )}

                        {/* Eligibility quick-glance */}
                        {showEligibility && areaPermissions.hasAny(CandidatesArea.read_basic, CandidatesArea.read_full) && (
                            <>
                                <Divider my={"1rem"} />
                                <HStack justify={"space-between"} mb={".5rem"}>
                                    <Heading variant={"info"}>Eligibility</Heading>
                                    {areaPermissions.has(CandidatesArea.edit) && (
                                        <Tooltip label="Edit eligibility">
                                            <IconButton
                                                size={"sm"}
                                                icon={<Icon as={IconEdit} />}
                                                aria-label={"Edit Eligibility"}
                                                onClick={eligibilityEdit.onOpen}
                                            />
                                        </Tooltip>
                                    )}
                                </HStack>
                                <Grid gridTemplateColumns={"auto 1fr auto 1fr"} columnGap={"1.5rem"} rowGap={".35rem"} alignItems={"center"}>
                                    <GridItem>
                                        <Text variant={"label"}>Qualifications:</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Shimmer isLoading={candidateLoading}>
                                            {areaPermissions.has(CandidatesArea.read_full)
                                                ? candidate?.qualifications?.map(q => displayQualification(q as CandidateQualification)).join(", ")
                                                : showMissing(null, "withheld")}
                                        </Shimmer>
                                    </GridItem>
                                    <GridItem>
                                        <Text variant={"label"}>Days Remaining:</Text>
                                    </GridItem>
                                    <GridItem>
                                        {candidate && candidate.daysRemaining >= 0 && candidate.daysRemaining !== null && (
                                            <Badge
                                                colorScheme={candidate?.daysRemaining > 21 ? "green" : candidate?.daysRemaining > 14 ? "yellow" : "red"}
                                                variant={"solid"}
                                                title={formatDateStr(candidate?.targetDate, dateFormat)}
                                            >
                                                {candidate?.daysRemaining}
                                            </Badge>
                                        )}
                                    </GridItem>
                                    <GridItem>
                                        <Text variant={"label"}>Target Qualification:</Text>
                                    </GridItem>
                                    <GridItem colSpan={3}>
                                        <Shimmer isLoading={candidateLoading}>
                                            {areaPermissions.has(CandidatesArea.read_full) && (
                                                <HStack>
                                                    {showMissing(
                                                        candidate?.selectedQualification &&
                                                            displayQualification(candidate?.selectedQualification as CandidateQualification),
                                                        "Not Selected"
                                                    )}
                                                    {!candidate?.selectedQualification && <Badge colorScheme={"red"}>Action Needed</Badge>}
                                                </HStack>
                                            )}
                                        </Shimmer>
                                    </GridItem>
                                    <GridItem>
                                        <Text variant={"label"}>Questionnaire Completion Date:</Text>
                                    </GridItem>
                                    <GridItem colSpan={3}>
                                        <Shimmer isLoading={candidateLoading}>{formatDateStr(candidate?.questionnaireCompletedOn, dateFormat)}</Shimmer>
                                    </GridItem>
                                </Grid>
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* ── Tabbed Content Area ───────────────────────────────────── */}
                <Tabs isLazy variant={"enclosed"} colorScheme={"blue"} index={tabIndex} onChange={handleTabChange}>
                    <TabList>
                        {showEmployment && areaPermissions.has(CandidatesArea.read_full) && <Tab>Details</Tab>}
                        {areaPermissions.has(CandidatesArea.request_mgmt) && (
                            <Tab>
                                Documents
                                {documentCount > 0 && (
                                    <Badge ml={2} colorScheme={"orange"} borderRadius={"full"}>
                                        {documentCount}
                                    </Badge>
                                )}
                            </Tab>
                        )}
                        {areaPermissions.hasAny(CandidatesArea.read_basic, CandidatesArea.read_full) && <Tab>Timeline</Tab>}
                        {areaPermissions.has(CandidatesArea.questionnaire) && <Tab>Questionnaire</Tab>}
                        <Tab>
                            Comments
                            {commentCount > 0 && (
                                <Badge ml={2} colorScheme={"blue"} borderRadius={"full"}>
                                    {commentCount}
                                </Badge>
                            )}
                        </Tab>
                    </TabList>

                    <TabPanels>
                        {/* Details tab */}
                        {showEmployment && areaPermissions.has(CandidatesArea.read_full) && (
                            <TabPanel px={0}>
                                <Card variant={"withShadow"}>
                                    <CardBody p={"2rem"}>
                                        <HStack justify={"space-between"} mb={".75rem"}>
                                            <Heading variant={"info"}>Employment</Heading>
                                            {areaPermissions.has(CandidatesArea.edit) && (
                                                <Tooltip label="Edit employment">
                                                    <IconButton
                                                        size={"sm"}
                                                        icon={<Icon as={IconEdit} />}
                                                        aria-label={"Edit Employment"}
                                                        onClick={employmentEdit.onOpen}
                                                    />
                                                </Tooltip>
                                            )}
                                        </HStack>
                                        <Grid gridTemplateColumns={"auto 1fr auto 1fr"} columnGap={"1rem"} rowGap={".25rem"} alignItems={"center"}>
                                            <GridItem>
                                                <Text variant={"label"}>Employee Number:</Text>
                                            </GridItem>
                                            <GridItem>{candidate?.employmentEmployeeId}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Employer Identification Number:</Text>
                                            </GridItem>
                                            <GridItem>{candidate?.employmentEIN}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Status:</Text>
                                            </GridItem>
                                            <GridItem>{displayEmploymentStatus(candidate?.employmentStatus)}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Job Title:</Text>
                                            </GridItem>
                                            <GridItem>{candidate?.employmentJobTitle}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Hire Date:</Text>
                                            </GridItem>
                                            <GridItem>{formatDateStr(candidate?.employmentHireDate, dateFormat)}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Start Date:</Text>
                                            </GridItem>
                                            <GridItem>{formatDateStr(candidate?.employmentStartDate, dateFormat)}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Offer Date:</Text>
                                            </GridItem>
                                            <GridItem>{formatDateStr(candidate?.employmentOfferDate, dateFormat)}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Termination Date:</Text>
                                            </GridItem>
                                            <GridItem>{formatDateStr(candidate?.employmentTerminationDate, dateFormat)}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Entity:</Text>
                                            </GridItem>
                                            <GridItem>{candidate?.entityName}</GridItem>
                                            <GridItem>
                                                <Text variant={"label"}>Last Payroll Date:</Text>
                                            </GridItem>
                                            <GridItem>{formatDateStr(candidate?.payrollMatchDate, dateFormat)}</GridItem>
                                        </Grid>

                                        <Divider my={"1rem"} />
                                        <CandidateWageHistorySection
                                            cId={cId}
                                            eId={eId}
                                            candidateId={candidateId}
                                            candidate={candidate}
                                            canEdit={areaPermissions.has(CandidatesArea.edit)}
                                        />

                                        {[1, 2, 3, 4, 5].some(
                                            n => candidateFieldsConfiguration?.[`customField0${n}DisplayName` as keyof typeof candidateFieldsConfiguration]
                                        ) && (
                                            <>
                                                <Divider my={"1rem"} />
                                                <HStack justify={"space-between"} mb={".75rem"}>
                                                    <Heading variant={"info"}>Additional</Heading>
                                                    {areaPermissions.has(CandidatesArea.edit) && (
                                                        <Tooltip label="Edit additional fields">
                                                            <IconButton
                                                                size={"sm"}
                                                                icon={<Icon as={IconEdit} />}
                                                                aria-label={"Edit Additional Fields"}
                                                                onClick={additionalEdit.onOpen}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </HStack>
                                                <Grid gridTemplateColumns={"auto 1fr"} columnGap={"1rem"} rowGap={".25rem"} alignItems={"center"}>
                                                    {(
                                                        [
                                                            ["customField01DisplayName", "customField01"],
                                                            ["customField02DisplayName", "customField02"],
                                                            ["customField03DisplayName", "customField03"],
                                                            ["customField04DisplayName", "customField04"],
                                                            ["customField05DisplayName", "customField05"],
                                                        ] as const
                                                    ).map(([displayKey, valueKey]) => {
                                                        const label = candidateFieldsConfiguration?.[displayKey];
                                                        if (!label) return null;
                                                        return (
                                                            <Fragment key={valueKey}>
                                                                <GridItem>
                                                                    <Text variant={"label"}>{label}:</Text>
                                                                </GridItem>
                                                                <GridItem>{candidate?.candidateCustomFields?.[valueKey]}</GridItem>
                                                            </Fragment>
                                                        );
                                                    })}
                                                </Grid>
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            </TabPanel>
                        )}

                        {/* Documents tab */}
                        {areaPermissions.has(CandidatesArea.request_mgmt) && (
                            <TabPanel px={0}>
                                <Card variant={"withShadow"}>
                                    <CardBody p={"2rem"}>
                                        <TableResult query={requestsQuery} columns={["Request", "File name", "Status", "Files", "Actions"]}>
                                            {candidateId &&
                                                requests?.map(request => {
                                                    if (request.documents?.length) {
                                                        return request.documents.map(document => (
                                                            <Tr key={`${document.id}-${request.id}`}>
                                                                <Td>{request.name}</Td>
                                                                <Td>
                                                                    <HStack align={"start"}>
                                                                        <Box alignSelf={"start"} pt={"1px"}>
                                                                            {getStatusIcon(request.status)}
                                                                        </Box>
                                                                        <Box>
                                                                            <Text color={"blackAlpha.800"} variant={"label"}>
                                                                                {request.preferred}
                                                                            </Text>
                                                                            <Link
                                                                                aria-label={"Open this file"}
                                                                                onClick={() => {
                                                                                    setSelectedDocumentId(document.id);
                                                                                    pdfViewerModal.onOpen();
                                                                                }}
                                                                                style={{ textDecoration: "underline" }}
                                                                            >
                                                                                {document.name}
                                                                            </Link>
                                                                        </Box>
                                                                    </HStack>
                                                                </Td>
                                                                <Td>{displayRequestStatus(request.status)}</Td>
                                                                <Td>{request.documents?.length ? "Uploaded" : <Badge colorScheme={"red"}>Missing</Badge>}</Td>
                                                                <Td>
                                                                    <IconButton
                                                                        icon={<Icon as={IconEdit} />}
                                                                        aria-label={"Edit Request"}
                                                                        onClick={() => {
                                                                            setSelectedRequestId(request.id);
                                                                            requestEditModal.onOpen();
                                                                        }}
                                                                        style={{ marginRight: "0.5rem" }}
                                                                    />
                                                                    <DownloadLink
                                                                        downloadFn={async progressCallback => {
                                                                            const blob = await downloadFn(
                                                                                cId,
                                                                                eId,
                                                                                candidateId!,
                                                                                request.id,
                                                                                document.id,
                                                                                progressCallback
                                                                            );
                                                                            return { blob, fileName: `${document.name}` };
                                                                        }}
                                                                    >
                                                                        <IconButton icon={<Icon as={IconDownload} />} aria-label={"Download File"} />
                                                                    </DownloadLink>
                                                                </Td>
                                                            </Tr>
                                                        ));
                                                    } else {
                                                        return (
                                                            <Tr key={`${request.id}`}>
                                                                <Td>{request.name}</Td>
                                                                <Td>
                                                                    <HStack align={"start"}>
                                                                        <Box alignSelf={"start"} pt={"1px"}>
                                                                            {getStatusIcon(request.status)}
                                                                        </Box>
                                                                        <Box>
                                                                            <Text color={"blackAlpha.800"} variant={"label"}>
                                                                                {request.preferred}
                                                                            </Text>
                                                                            <Text>{request.name}</Text>
                                                                        </Box>
                                                                    </HStack>
                                                                </Td>
                                                                <Td>{displayRequestStatus(request.status)}</Td>
                                                                <Td>
                                                                    <Badge colorScheme={"red"}>Missing</Badge>
                                                                </Td>
                                                                <Td>
                                                                    <IconButton
                                                                        icon={<Icon as={IconEdit} />}
                                                                        aria-label={"Edit Request"}
                                                                        onClick={() => {
                                                                            setSelectedRequestId(request.id);
                                                                            requestEditModal.onOpen();
                                                                        }}
                                                                    />
                                                                </Td>
                                                            </Tr>
                                                        );
                                                    }
                                                })}
                                        </TableResult>
                                        <Flex justifyContent="flex-end" mt={"1.5rem"} width={"full"}>
                                            <Button
                                                variant={"secondary"}
                                                mr={3}
                                                onClick={() => generate8850.mutate({ clientId: cId!, entityId: eId!, candidateId: candidateId! })}
                                                isLoading={generate8850.isLoading}
                                                isDisabled={!candidate || candidateLoading}
                                            >
                                                Generate 8850
                                            </Button>
                                            <Button variant={"primary"} mr={3} onClick={createRequestModal.onOpen} isDisabled={!candidate || candidateLoading}>
                                                Create Request
                                            </Button>
                                        </Flex>
                                    </CardBody>
                                </Card>
                            </TabPanel>
                        )}

                        {/* Timeline tab */}
                        {areaPermissions.hasAny(CandidatesArea.read_basic, CandidatesArea.read_full) && (
                            <TabPanel px={0}>
                                <Card variant={"withShadow"}>
                                    <CardBody p={"2rem"}>
                                        <Shimmer isLoading={candidateTimelineQuery.isLoading}>
                                            <CandidateTimeline candidateActivity={candidateTimelineQuery.data} candidate={candidate} />
                                        </Shimmer>
                                    </CardBody>
                                </Card>
                            </TabPanel>
                        )}

                        {/* Questionnaire tab */}
                        {areaPermissions.has(CandidatesArea.questionnaire) && (
                            <TabPanel px={0}>
                                <CandidateQuestionnaire />
                            </TabPanel>
                        )}

                        {/* Comments tab */}
                        <TabPanel px={0}>
                            <Card variant={"withShadow"}>
                                <CardBody p={"2rem"}>
                                    <form>
                                        <Stack spacing={".5rem"}>
                                            {candidateComments.data?.map(comment => (
                                                <CandidateCommentCards
                                                    key={comment.id}
                                                    tenantId={cId as string}
                                                    entityId={eId as string}
                                                    {...comment}
                                                    refetch={candidateComments.refetch}
                                                />
                                            ))}
                                        </Stack>
                                        <Divider my={"1rem"} />
                                        <Stack>
                                            <Textarea
                                                placeholder="Add a comment."
                                                size="md"
                                                resize="none"
                                                value={comment}
                                                onChange={e => setComment(e.target.value)}
                                            />
                                        </Stack>
                                        <Flex justifyContent="flex-end" mt={"1.5rem"} width={"full"}>
                                            <Button
                                                variant={"primary"}
                                                mr={3}
                                                onClick={() => createCandidateComment(comment)}
                                                isDisabled={!comment}
                                                isLoading={saveCandidateComment.isLoading}
                                                loadingText={"Creating"}
                                            >
                                                Post
                                            </Button>
                                            <Button variant="ghost" onClick={() => setComment("")}>
                                                Cancel
                                            </Button>
                                        </Flex>
                                    </form>
                                </CardBody>
                            </Card>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Flex>

            {/* ── Section Edit Modals ───────────────────────────────────── */}
            <CreateRequestModal isOpen={createRequestModal.isOpen} onClose={createRequestModal.onClose} />
            <RequestModal isOpen={requestEditModal.isOpen} onClose={requestEditModal.onClose} requestId={selectedRequestId} />
            <CandidateDetailsPdfViewerModal isOpen={pdfViewerModal.isOpen} onClose={pdfViewerModal.onClose} documentId={selectedDocumentId} />
            <CandidateGeneralModal
                isOpen={generalEdit.isOpen}
                onClose={generalEdit.onClose}
                candidate={candidate}
                cId={cId}
                eId={eId}
                candidateId={candidateId}
            />
            <CandidateEligibilityModal
                isOpen={eligibilityEdit.isOpen}
                onClose={eligibilityEdit.onClose}
                candidate={candidate}
                cId={cId}
                eId={eId}
                candidateId={candidateId}
            />
            <CandidateEmploymentModal
                isOpen={employmentEdit.isOpen}
                onClose={employmentEdit.onClose}
                candidate={candidate}
                cId={cId}
                eId={eId}
                candidateId={candidateId}
            />
            {[1, 2, 3, 4, 5].some(n => candidateFieldsConfiguration?.[`customField0${n}DisplayName` as keyof typeof candidateFieldsConfiguration]) && (
                <CandidateAdditionalModal
                    isOpen={additionalEdit.isOpen}
                    onClose={additionalEdit.onClose}
                    candidate={candidate}
                    cId={cId}
                    eId={eId}
                    candidateId={candidateId}
                    candidateFieldsConfiguration={candidateFieldsConfiguration}
                />
            )}
        </>
    );
};

const formatCandidateAddressLine3 = (candidate: EntityCandidate) => {
    const formattedZipCode = candidate?.zipCode?.length === 9 ? `${candidate.zipCode.substring(0, 5)}-${candidate.zipCode.substring(5)}` : candidate?.zipCode;
    return `${candidate?.city}, ${candidate?.state} ${formattedZipCode}`;
};
